import styled from '@emotion/styled';
import { ContentBanner } from '@modernhealth/vital-components';
import { mediaQuery } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';

import { mediaQueryMaxWidth } from 'src/shared/utils';

const BackgroundImage =
  'https://assets.joinmodernhealth.com/circles/hero/circles-hero-optimized.jpg';

/**
 * A simple utilty to build CSS linear gradients.
 *
 * Color stops can be specified as tuples with a color
 * and a start point. Percentage values can be specified
 * as decimals.
 */
export function linearGradient(
  deg: number,
  ...colors: [color: string, coverage: number | string][]
) {
  const colorDefinitions = colors
    .map(
      ([color, coverage]) =>
        `${color} ${typeof coverage === 'string' ? coverage : `${(coverage * 100).toFixed(0)}%`}`,
    )
    .join(',');

  return `linear-gradient(${deg.toFixed(2)}deg, ${colorDefinitions})`;
}

// Banner gradiant for tablet and desktop screen sizes
const bannerGradient = linearGradient(
  89.77,
  ['#0B4054', 'max(250px, 25%)'],
  ['rgba(25, 49, 58, 0.46)', 0.42],
  ['rgba(37, 36, 36, 0)', 0.49],
);
// Banner gradient for medium screens
const bannerGradientMd = linearGradient(89.77, ['#0B4054', 0.4], ['rgba(25, 49, 58, 0)', 0.64]);
// Banner gradient for small screens
const bannerGradientSm = linearGradient(
  89.77,
  ['#0B4054', 'max(100px, 40%)'],
  ['rgba(25, 49, 58, 0)', 0.64],
);

const BannerBase = styled(ContentBanner)({
  backgroundImage: `${bannerGradient}, url(${BackgroundImage})`,
  backgroundPosition: '0 0, 250px 33%',
  [mediaQuery.maxWidth('md')]: {
    backgroundImage: `${bannerGradientMd}, url(${BackgroundImage})`,
    backgroundPosition: '0 0, 150px 33%',
  },
  [mediaQuery.maxWidth('sm')]: {
    backgroundImage: `${bannerGradientSm}, url(${BackgroundImage})`,
    backgroundPosition: '0 0, 125px 33%',
  },
  [mediaQuery.maxWidth('xs')]: {
    backgroundPosition: '0 0, 50px 33%',
  },
  // Fallback to solid color on very small screens
  [mediaQueryMaxWidth(300)]: {
    background: '#0B4054',
  },
  backgroundRepeat: 'no-repeat',
  backgroundSize: '100% 100%, cover',
  // !important is required to override the ContentBanner's minHeight
  // TODO: Expose "primitive" versions of containers in Vita (e.g. BannerPattern)
  minHeight: '283px !important',
  marginBottom: '23px',
});

export const Banner: React.FC<{
  title: string;
  description: string;
  buttonTitle: string;
}> = props => {
  const analytics = useAnalytics();

  return (
    <BannerBase
      backgroundColor="gray85"
      theme="light"
      onClick={() => {
        // TODO: Ensure Vita allows click tracking on links
        analytics.trackClick('Circles: Banner CTA', {
          destinationPath: '/about-circles',
        });
      }}
      linkTo="/about-circles"
      buttonProps={{
        variant: 'flat',
      }}
      {...props}
    />
  );
};
